import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { BarChart, LineChart } from 'react-native-chart-kit';
import SectionCard from '../components/SectionCard';
import StatCard from '../components/StatCard';
import TransactionRow from '../components/TransactionRow';
import DateFilterBar from '../components/DateFilterBar';
import { fetchAnalytics } from '../lib/api';
import { currency, percent, shortDate } from '../lib/format';

const chartConfig = {
  backgroundGradientFrom: '#ffffff',
  backgroundGradientTo: '#ffffff',
  decimalPlaces: 0,
  color: (opacity = 1) => `rgba(79, 70, 229, ${opacity})`,
  labelColor: (opacity = 1) => `rgba(71, 85, 105, ${opacity})`,
  propsForBackgroundLines: { stroke: '#e2e8f0' },
};

const periodOptions = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

const pageSize = 8;

function Pager({ page, total, onPrevious, onNext }) {
  if (total <= pageSize) return null;
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  return (
    <View className="mt-2 flex-row items-center justify-between">
      <TouchableOpacity className={`rounded-xl px-4 py-2 ${page <= 1 ? 'bg-slate-100' : 'bg-slate-900'}`} disabled={page <= 1} onPress={onPrevious}>
        <Text className={`font-bold ${page <= 1 ? 'text-slate-400' : 'text-white'}`}>Previous</Text>
      </TouchableOpacity>
      <Text className="font-bold text-slate-600">Page {page} of {totalPages}</Text>
      <TouchableOpacity className={`rounded-xl px-4 py-2 ${page >= totalPages ? 'bg-slate-100' : 'bg-slate-900'}`} disabled={page >= totalPages} onPress={onNext}>
        <Text className={`font-bold ${page >= totalPages ? 'text-slate-400' : 'text-white'}`}>Next</Text>
      </TouchableOpacity>
    </View>
  );
}

export default function AnalyticsScreen() {
  const { width } = useWindowDimensions();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ start: '', end: '' });
  const [appliedFilters, setAppliedFilters] = useState({ start: '', end: '' });
  const [pages, setPages] = useState({ daily: 1, category: 1, item: 1, biggest: 1, recurring: 1, overspend: 1 });

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      setAnalytics(await fetchAnalytics(appliedFilters));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [appliedFilters]);

  useFocusEffect(useCallback(() => {
    load(false);
  }, [load]));

  const chartWidth = Math.max(280, Math.min(width - 48, 840));
  const periodSummary = useMemo(() => {
    if (!analytics) return [];
    if (period === 'weekly') return analytics.weeklySummary || [];
    if (period === 'monthly') return analytics.monthlySummary || [];
    return analytics.dailySummary || [];
  }, [analytics, period]);

  const resetPages = () => setPages({ daily: 1, category: 1, item: 1, biggest: 1, recurring: 1, overspend: 1 });
  const pageSlice = (rows, key) => rows.slice((pages[key] - 1) * pageSize, pages[key] * pageSize);
  const changePage = (key, direction, total) => {
    const totalPages = Math.max(Math.ceil(total / pageSize), 1);
    setPages((current) => ({ ...current, [key]: Math.min(Math.max(current[key] + direction, 1), totalPages) }));
  };

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center bg-slate-50">
        <ActivityIndicator size="large" color="#4f46e5" />
        <Text className="mt-4 text-slate-600">Preparing analysis...</Text>
      </View>
    );
  }

  const chartRows = periodSummary.slice(-8);
  const labels = chartRows.map((row) => (row.date ? shortDate(row.date) : row.period));
  const expenses = chartRows.map((row) => row.expense || 0);
  const income = chartRows.map((row) => row.income || 0);
  const balance = (analytics?.dailySummary || []).slice(-10);
  const balanceLabels = balance.map((row) => shortDate(row.date));
  const balanceValues = balance.map((row) => row.balanceLeft || 0);
  const dailyRows = (analytics?.dailySummary || []).slice().reverse();
  const categoryRows = analytics?.categoryBreakdown?.[period] || [];
  const itemRows = analytics?.itemBreakdown?.[period] || [];
  const biggestRows = analytics?.biggestTransactions || [];
  const recurringRows = analytics?.expenseOutlook?.recurringBreakdown || [];
  const dailyLimit = analytics?.dailyExpenseLimit || null;
  const overSpendRows = dailyRows.filter((day) => day.overDailyMax);
  const overSpendChartRows = (analytics?.dailySummary || []).filter((day) => day.overDailyMax).slice(-8);
  const overSpendLabels = overSpendChartRows.map((day) => shortDate(day.date));
  const overSpendData = overSpendChartRows.map((day) => day.overLimitAmount || 0);
  const pagedDailyRows = pageSlice(dailyRows, 'daily');
  const pagedCategoryRows = pageSlice(categoryRows, 'category');
  const pagedItemRows = pageSlice(itemRows, 'item');
  const pagedBiggestRows = pageSlice(biggestRows, 'biggest');
  const pagedRecurringRows = pageSlice(recurringRows, 'recurring');
  const pagedOverSpendRows = pageSlice(overSpendRows, 'overspend');
  const outlook = analytics?.expenseOutlook || {};

  return (
    <ScrollView
      className="flex-1 bg-slate-50"
      contentContainerStyle={{ padding: 16, paddingBottom: 36 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
    >
      <View className="rounded-2xl bg-indigo-950 p-5">
        <Text className="text-sm font-semibold uppercase text-indigo-200">Complete finance analysis</Text>
        <Text className="mt-2 text-3xl font-black text-white">Spending patterns, loss days, and money consumers.</Text>
        <Text className="mt-3 text-base text-indigo-100">
          Review what you make, what you spend, what gets carried forward, and which activities consume the most money.
        </Text>
      </View>

      {error ? (
        <View className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <Text className="font-semibold text-rose-700">{error}</Text>
        </View>
      ) : null}

      <View className="mt-4">
        <DateFilterBar
          filters={filters}
          onChange={setFilters}
          onApply={() => {
            resetPages();
            setAppliedFilters(filters);
          }}
          onClear={() => {
            resetPages();
            setFilters({ start: '', end: '' });
            setAppliedFilters({ start: '', end: '' });
          }}
        />
      </View>

      <View className="mt-4 flex-row flex-wrap gap-3">
        <View className="min-w-[150px] flex-1">
          <StatCard label="Net cash flow" value={currency(analytics?.totals?.netCashFlow)} subtitle="Income minus spending" icon="account-balance-wallet" tone="indigo" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Spend ratio" value={percent(analytics?.totals?.spendRatio)} subtitle="Expense against income" icon="percent" tone="red" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Average expense" value={currency(analytics?.totals?.averageDailyExpense)} subtitle="Per tracked day" icon="today" tone="amber" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Transactions" value={String(analytics?.transactionCount || 0)} subtitle="Ledger entries" icon="receipt-long" tone="cyan" />
        </View>
      </View>

      {dailyLimit ? (
        <View className="mt-4 flex-row flex-wrap gap-3">
          <View className="min-w-[150px] flex-1">
            <StatCard label="Daily max" value={currency(dailyLimit.amount)} subtitle="Actual expenses only" icon="speed" tone="cyan" />
          </View>
          <View className="min-w-[150px] flex-1">
            <StatCard label="Over days" value={String(dailyLimit.daysOverLimit || 0)} subtitle={`${dailyLimit.trackedDays || 0} tracked day(s)`} icon="warning" tone="red" />
          </View>
          <View className="min-w-[150px] flex-1">
            <StatCard label="Total over" value={currency(dailyLimit.totalOverLimit)} subtitle="Above daily max" icon="trending-up" tone="red" />
          </View>
          <View className="min-w-[150px] flex-1">
            <StatCard label="Today left" value={dailyLimit.todayOverLimit ? currency(0) : currency(dailyLimit.todayRemaining)} subtitle={dailyLimit.todayOverLimit ? `${currency(dailyLimit.todaySpent - dailyLimit.amount)} over today` : `${percent(dailyLimit.todayPercent)} used`} icon="today" tone={dailyLimit.todayOverLimit ? 'red' : 'green'} />
          </View>
        </View>
      ) : null}

      <View className="mt-4 flex-row flex-wrap gap-3">
        <View className="min-w-[150px] flex-1">
          <StatCard label="Actual earned" value={currency(outlook.actualIncome)} subtitle={`${outlook.range?.days || 0} day time frame`} icon="payments" tone="green" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Actual spent" value={currency(outlook.actualExpense)} subtitle="Recorded expenses" icon="shopping-cart" tone="red" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Recurring projected" value={currency(outlook.recurringProjected)} subtitle={`${currency(outlook.recurringMonthlyEstimate)} monthly run-rate`} icon="event-repeat" tone="amber" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Expense forecast" value={currency(outlook.projectedExpense)} subtitle="Actual + pending + recurring" icon="query-stats" tone="cyan" />
        </View>
        <View className="min-w-[150px] flex-1">
          <StatCard label="Projected net" value={currency(outlook.projectedNetCashFlow)} subtitle={`${percent(outlook.projectedSpendRatio)} projected spend`} icon="timeline" tone="indigo" />
        </View>
      </View>

      <View className="mt-4 rounded-2xl border border-slate-200 bg-white p-2">
        <View className="flex-row gap-2">
          {periodOptions.map((option) => (
            <TouchableOpacity
              key={option.key}
              className={`flex-1 rounded-xl px-3 py-3 ${period === option.key ? 'bg-slate-950' : 'bg-white'}`}
              onPress={() => {
                resetPages();
                setPeriod(option.key);
              }}
            >
              <Text className={`text-center font-bold ${period === option.key ? 'text-white' : 'text-slate-600'}`}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View className="mt-4">
        <SectionCard title="Income vs Spending" subtitle={`Showing the last ${chartRows.length} ${period} period(s).`}>
          {chartRows.length ? (
            <BarChart
              data={{
                labels,
                datasets: [{ data: expenses }],
              }}
              width={chartWidth}
              height={230}
              yAxisLabel="$"
              chartConfig={{ ...chartConfig, color: (opacity = 1) => `rgba(225, 29, 72, ${opacity})` }}
              fromZero
              showValuesOnTopOfBars
              style={{ borderRadius: 12, marginLeft: -12 }}
            />
          ) : <Text className="text-slate-500">No data for this period yet.</Text>}
          {chartRows.length ? (
            <View className="mt-2 flex-row flex-wrap gap-3">
              <Text className="text-sm font-semibold text-emerald-700">Income: {currency(income.reduce((sum, value) => sum + value, 0))}</Text>
              <Text className="text-sm font-semibold text-rose-700">Spending: {currency(expenses.reduce((sum, value) => sum + value, 0))}</Text>
            </View>
          ) : null}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Expense Forecast Sync" subtitle="Actual spending, expense plans, and recurring commitments for the selected time frame.">
          <View className="gap-3">
            <View className="rounded-2xl bg-slate-50 p-4">
              <Text className="font-black text-slate-950">Recorded vs expected</Text>
              <View className="mt-3 flex-row flex-wrap gap-x-5 gap-y-2">
                <Text className="text-sm font-semibold text-rose-700">Actual expenses: {currency(outlook.actualExpense)}</Text>
                <Text className="text-sm font-semibold text-amber-700">Pending planned: {currency(outlook.plannedPending)}</Text>
                <Text className="text-sm font-semibold text-indigo-700">Recurring projection: {currency(outlook.recurringProjected)}</Text>
                <Text className="text-sm font-semibold text-slate-900">Projected total: {currency(outlook.projectedExpense)}</Text>
              </View>
            </View>
            <View className="rounded-2xl bg-slate-50 p-4">
              <Text className="font-black text-slate-950">Income pressure</Text>
              <Text className="mt-2 text-sm text-slate-600">
                You earned {currency(outlook.actualIncome)} and recorded {currency(outlook.actualExpense)} in expenses. With pending plans and recurring commitments included, projected net is {currency(outlook.projectedNetCashFlow)}.
              </Text>
            </View>
          </View>
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Over-Spending Analysis" subtitle="Days where actual recorded expenses exceeded your daily maximum. Planned expenses are excluded.">
          {dailyLimit ? (
            <>
              {overSpendChartRows.length ? (
                <BarChart
                  data={{ labels: overSpendLabels, datasets: [{ data: overSpendData }] }}
                  width={chartWidth}
                  height={230}
                  yAxisLabel="$"
                  chartConfig={{ ...chartConfig, color: (opacity = 1) => `rgba(225, 29, 72, ${opacity})` }}
                  fromZero
                  showValuesOnTopOfBars
                  style={{ borderRadius: 12, marginLeft: -12 }}
                />
              ) : (
                <View className="rounded-2xl bg-emerald-50 p-4">
                  <Text className="font-black text-emerald-800">No over-spending days in this time frame.</Text>
                  <Text className="mt-1 text-sm text-emerald-700">Your actual expenses stayed within the daily maximum.</Text>
                </View>
              )}

              <View className="mt-4">
                {pagedOverSpendRows.map((day) => (
                  <View key={day.date} className="mb-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
                    <View className="flex-row items-center justify-between gap-3">
                      <View className="flex-1">
                        <Text className="font-black text-rose-950">{shortDate(day.date)}</Text>
                        <Text className="mt-1 text-sm text-rose-700">
                          Spent {currency(day.expense)} against a daily max of {currency(day.dailyMaxExpense)}.
                        </Text>
                      </View>
                      <Text className="font-black text-rose-700">{currency(day.overLimitAmount)}</Text>
                    </View>
                    <View className="mt-3 h-2 overflow-hidden rounded-full bg-white">
                      <View className="h-2 rounded-full bg-rose-600" style={{ width: `${Math.min(day.dailyMaxPercent || 0, 100)}%` }} />
                    </View>
                    <Text className="mt-2 text-sm font-semibold text-rose-700">{percent(day.dailyMaxPercent)} of daily maximum used</Text>
                  </View>
                ))}
                <Pager page={pages.overspend} total={overSpendRows.length} onPrevious={() => changePage('overspend', -1, overSpendRows.length)} onNext={() => changePage('overspend', 1, overSpendRows.length)} />
              </View>
            </>
          ) : (
            <Text className="text-slate-500">Set a daily maximum expense on the Home screen to unlock over-spending charts.</Text>
          )}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Brought Forward Balance" subtitle="Daily closing balance after the previous day is carried forward.">
          {balance.length ? (
            <LineChart
              data={{ labels: balanceLabels, datasets: [{ data: balanceValues }] }}
              width={chartWidth}
              height={230}
              yAxisLabel="$"
              chartConfig={chartConfig}
              bezier
              style={{ borderRadius: 12, marginLeft: -12 }}
            />
          ) : <Text className="text-slate-500">No balance history yet.</Text>}
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Daily Ledger Analysis" subtitle="Income, expense, brought forward, balance left, loss, and spend percentage.">
          {pagedDailyRows.map((day) => (
            <View key={day.date} className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <View className="flex-row items-center justify-between">
                <Text className="font-black text-slate-950">{shortDate(day.date)}</Text>
                <Text className="font-bold text-slate-700">{percent(day.spendPercent)} spent</Text>
              </View>
              <View className="mt-3 flex-row flex-wrap gap-x-5 gap-y-2">
                <Text className="text-sm text-slate-600">B/F: {currency(day.broughtForward)}</Text>
                <Text className="text-sm text-emerald-700">Earned: {currency(day.income)}</Text>
                <Text className="text-sm text-rose-700">Spent: {currency(day.expense)}</Text>
                <Text className="text-sm text-slate-900">Left: {currency(day.balanceLeft)}</Text>
                <Text className="text-sm text-amber-700">Loss: {currency(day.amountLoss)}</Text>
              </View>
            </View>
          ))}
          {!analytics?.dailySummary?.length ? <Text className="text-slate-500">No daily rows yet.</Text> : null}
          <Pager page={pages.daily} total={dailyRows.length} onPrevious={() => changePage('daily', -1, dailyRows.length)} onNext={() => changePage('daily', 1, dailyRows.length)} />
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Category Consumers" subtitle={`Top categories by ${period} spending.`}>
          {pagedCategoryRows.map((row) => (
            <View key={`${row.category}-${row.period}`} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">{row.category}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{row.period} • {row.count} transaction(s)</Text>
                </View>
                <Text className="font-black text-rose-600">{currency(row.expense)}</Text>
              </View>
              {row.topItems?.length ? (
                <Text className="mt-2 text-sm text-slate-600">Top: {row.topItems.map((item) => `${item.item} ${currency(item.amount)}`).join(', ')}</Text>
              ) : null}
            </View>
          ))}
          {!categoryRows.length ? <Text className="text-slate-500">No category spending yet.</Text> : null}
          <Pager page={pages.category} total={categoryRows.length} onPrevious={() => changePage('category', -1, categoryRows.length)} onNext={() => changePage('category', 1, categoryRows.length)} />
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Item And Activity Costs" subtitle={`What specific things cost you most ${period}.`}>
          {pagedItemRows.map((row) => (
            <View key={`${row.item}-${row.period}-${row.category}`} className="mb-3 flex-row items-center justify-between rounded-2xl bg-slate-50 p-4">
              <View className="flex-1">
                <Text className="font-black text-slate-950">{row.item}</Text>
                <Text className="mt-1 text-sm text-slate-500">{row.category} • {row.period} • {row.count} time(s)</Text>
              </View>
              <Text className="font-black text-rose-600">{currency(row.amount)}</Text>
            </View>
          ))}
          {!itemRows.length ? <Text className="text-slate-500">No item spending yet.</Text> : null}
          <Pager page={pages.item} total={itemRows.length} onPrevious={() => changePage('item', -1, itemRows.length)} onNext={() => changePage('item', 1, itemRows.length)} />
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Recurring Balance" subtitle="Recurring commitments projected into the selected time frame.">
          {pagedRecurringRows.map((row) => (
            <View key={row.id} className="mb-3 rounded-2xl bg-slate-50 p-4">
              <View className="flex-row items-center justify-between gap-3">
                <View className="flex-1">
                  <Text className="font-black text-slate-950">{row.item}</Text>
                  <Text className="mt-1 text-sm text-slate-500">{row.category} | {row.frequency} | next due {row.next_due_date}</Text>
                </View>
                <Text className="font-black text-amber-700">{currency(row.projected_in_range)}</Text>
              </View>
              <Text className="mt-2 text-sm text-slate-600">Base amount: {currency(row.amount)}</Text>
            </View>
          ))}
          {!recurringRows.length ? <Text className="text-slate-500">No recurring expenses yet.</Text> : null}
          <Pager page={pages.recurring} total={recurringRows.length} onPrevious={() => changePage('recurring', -1, recurringRows.length)} onNext={() => changePage('recurring', 1, recurringRows.length)} />
        </SectionCard>
      </View>

      <View className="mt-4">
        <SectionCard title="Biggest Single Expenses" subtitle="The largest individual expenses in your ledger.">
          {pagedBiggestRows.map((tx) => <TransactionRow key={tx.id} transaction={tx} />)}
          {!analytics?.biggestTransactions?.length ? <Text className="text-slate-500">No expense transactions yet.</Text> : null}
          <Pager page={pages.biggest} total={biggestRows.length} onPrevious={() => changePage('biggest', -1, biggestRows.length)} onNext={() => changePage('biggest', 1, biggestRows.length)} />
        </SectionCard>
      </View>
    </ScrollView>
  );
}
